/**
 * Týždenný pohľad denného menu – LEN portrait obrazovka.
 *
 * Zobrazí dni aktuálneho týždňa, ktoré majú obsah (weekDays z useDailyMenuScreenData),
 * pre každý deň polievky a hlavné jedlá aktuálneho typu menu. Strieda sa s denným
 * pohľadom podľa nastavenia weeklyMenuRotation (rieši DailyMenuScreenPortrait).
 */
import React, { useMemo } from "react";
import { resolveLocaleValue } from "app/utils/resolveLocaleValue";

/** Lokalizované sekčné popisky (fallback SK). */
const SECTION_LABELS = {
  soups: {
    "sk-SK": "Polievky",
    "cs-CZ": "Polévky",
    "en-US": "Soups",
    "de-DE": "Suppen",
  },
  meals: {
    "sk-SK": "Hlavné jedlá",
    "cs-CZ": "Hlavní jídla",
    "en-US": "Main dishes",
    "de-DE": "Hauptgerichte",
  },
};

/** Fallback názvy dní (keď nie je dostupný dátum na Intl formátovanie). */
const DAY_LABELS_FALLBACK = {
  monday: "Pondelok",
  tuesday: "Utorok",
  wednesday: "Streda",
  thursday: "Štvrtok",
  friday: "Piatok",
  saturday: "Sobota",
  sunday: "Nedeľa",
};

function pickLabel(dict, locale) {
  return dict[locale] || dict["sk-SK"] || Object.values(dict)[0] || "";
}

function capitalize(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function formatDayHeading(day, locale) {
  const { date, dayKey } = day;
  if (date instanceof Date && !Number.isNaN(date.getTime())) {
    try {
      const weekday = capitalize(
        new Intl.DateTimeFormat(locale, { weekday: "long" }).format(date),
      );
      const dm = new Intl.DateTimeFormat(locale, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
        .format(date)
        .replace(/\s/g, "");
      return `${weekday} ${dm}`;
    } catch {
      /* fallthrough */
    }
  }
  return DAY_LABELS_FALLBACK[dayKey] || dayKey;
}

const WeeklyMenuPortrait = ({ weekDays, locale, defaultLocale }) => {
  const days = Array.isArray(weekDays) ? weekDays : [];
  const soupsLabel = useMemo(() => pickLabel(SECTION_LABELS.soups, locale), [locale]);
  const mealsLabel = useMemo(() => pickLabel(SECTION_LABELS.meals, locale), [locale]);

  const nameOf = (p) =>
    resolveLocaleValue(p?.title, locale, defaultLocale) || "";

  if (days.length === 0) return null;

  return (
    <div className="daily-menu-portrait__weekly">
      {days.map((day) => {
        const hasSoups = day.soups?.length > 0;
        const hasMeals = day.meals?.length > 0;
        return (
          <section
            key={day.dayKey}
            className="daily-menu-portrait__weekly-day"
          >
            <h2 className="daily-menu-portrait__weekly-day-title">
              {formatDayHeading(day, locale)}
            </h2>
            {hasSoups && (
              <div className="daily-menu-portrait__weekly-group">
                <div className="daily-menu-portrait__weekly-group-label">
                  {soupsLabel}
                </div>
                <ul className="daily-menu-portrait__weekly-list">
                  {day.soups.map((p, i) => (
                    <li
                      key={p?.id ?? `s-${i}`}
                      className={
                        p?.isActive === false
                          ? "daily-menu-portrait__weekly-item is-inactive"
                          : "daily-menu-portrait__weekly-item"
                      }
                    >
                      {nameOf(p)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {hasMeals && (
              <div className="daily-menu-portrait__weekly-group">
                <div className="daily-menu-portrait__weekly-group-label">
                  {mealsLabel}
                </div>
                <ul className="daily-menu-portrait__weekly-list">
                  {day.meals.map((p, i) => (
                    <li
                      key={p?.id ?? `m-${i}`}
                      className={
                        p?.isActive === false
                          ? "daily-menu-portrait__weekly-item is-inactive"
                          : "daily-menu-portrait__weekly-item"
                      }
                    >
                      {nameOf(p)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
};

export default WeeklyMenuPortrait;
